/**
 * PATH: packages/email-hub/src/feature/components/SuggestedJobChip.tsx
 * FILE_ID: EMAIL-INBOX-CHIP-SUGGEST-002
 * PURPOSE:
 *   - Inline "Suggested job" chip rendered on every unallocated
 *     inbox row when the BE attached a ``suggested_job`` block
 *     (see ``EmailListSerializer.get_suggested_job``) OR the lazy
 *     ``useInboxSuggestions`` backfill produced one.
 *
 * WHY (v002 — 2026-05-25):
 *   Operator report 2026-05-25: "where is the clear x and tick to
 *   accept it ... for the ai suggested kanban tagging.." The v001
 *   chip was a single big "accept" button with a baked-in ✓
 *   glyph — operators reasonably read the ✓ as already-accepted
 *   state, NOT as a click-to-confirm affordance, and there was no
 *   way to dismiss a wrong suggestion without opening the full
 *   Link-to-Job modal.
 *
 *   v002 splits the chip into:
 *     - informational label (sparkles + job_label)
 *     - ✓ accept button  → linkToJob(emailId, job_id)
 *     - ✕ reject button  → rejectSuggestion(emailId, job_id)
 *
 *   Matches the AllocationChip AI_SUGGESTED_PENDING pattern so
 *   operators learn ONE affordance ("amber dashed chip with ✓/✕
 *   = AI guess; click ✓ to accept, ✕ to suppress").
 *
 * FLOW:
 *   suggested_job (BE)
 *     → chip render
 *       → ✓ click → linkToJob → refetch
 *       → ✕ click → rejectSuggestion (writes denylist) → onDismissed
 *
 * BEHAVIOUR:
 *   - ✓ click stops propagation, calls linkToJob, toasts success,
 *     fires ``onConfirmed`` so the host refetches.
 *   - ✕ click stops propagation, calls rejectSuggestion, toasts
 *     success, fires ``onDismissed`` so the host can prune the
 *     local suggestions map (chip disappears IMMEDIATELY without
 *     waiting for a refetch — the denylist row stops the chip
 *     coming back on the next poll).
 *   - Both buttons disable during the in-flight request to prevent
 *     double-fires.
 *   - Spinner replaces the icon of the active button only.
 *
 * DEPENDENCIES:
 *   - useEmailJobLink: existing host hook. v002 extends it with
 *     ``rejectSuggestion`` (POST /api/emails/job-suggestion-rejections/).
 *   - useToast: surface success / error to the operator.
 *
 * RELATED:
 *   - EMAIL-HUB-INBOX-PAGE: the host that renders this chip per row
 *     and owns the lazy suggestions map (must accept onDismissed)
 *   - AllocationChip (kanban-host): mirrors the ✓/✕ pattern for
 *     already-allocated AI_SUGGESTED_PENDING rows
 */
import { useState } from 'react'
import { Sparkles, Loader2, Check, X } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'

import { useEmailJobLink, type SuggestedJob } from '../hooks/useEmails'

interface SuggestedJobChipProps {
  /** Email row id — passed straight to ``linkToJob`` /
   *  ``rejectSuggestion``. */
  emailId: string
  /** Suggestion payload from the BE. ``null`` / undefined → chip
   *  doesn't render (caller can pass it unconditionally). */
  suggested: SuggestedJob | null | undefined
  /** Fired after a successful accept so the host can refetch the
   *  inbox list (the row will then render the solid Job pill instead
   *  of the suggestion chip). Optional — toasts work without it. */
  onConfirmed?: () => void
  /** Fired after a successful reject so the host can drop the
   *  suggestion from its local map and hide the chip immediately
   *  (the denylist row on the BE will stop the chip coming back on
   *  the next refetch). Optional — toasts work without it. */
  onDismissed?: () => void
  /** Controlled mode (reader-header runtime, change #269): when provided,
   *  the ✓ press delegates ENTIRELY to the caller (optimistic paint, undo,
   *  classified errors) and the chip's internal transport/toasts are
   *  bypassed. Provide onReject with it — never mix modes. */
  onAccept?: () => void
  /** Controlled-mode ✕ counterpart of `onAccept`. */
  onReject?: () => void
}

/** Per-kind tooltip copy. Keeps the chip itself short while still
 *  letting the operator hover to learn why this suggestion exists. */
const KIND_LABEL: Record<SuggestedJob['kind'], string> = {
  thread: 'Same thread as an already-allocated email',
  sibling: 'Same conversation in another mailbox is allocated',
  matcher: 'Best keyword + sender-history match',
}

export function SuggestedJobChip({
  emailId,
  suggested,
  onConfirmed,
  onDismissed,
  onAccept,
  onReject,
}: SuggestedJobChipProps) {
  const { linkToJob, rejectSuggestion, isLinking } = useEmailJobLink()
  const { toast } = useToast()
  const [acceptBusy, setAcceptBusy] = useState(false)
  const [rejectBusy, setRejectBusy] = useState(false)

  if (!suggested) return null

  const handleAccept = async (event: React.MouseEvent) => {
    event.stopPropagation()
    if (onAccept) {
      // Controlled: the runner acks in the same frame — no local busy state.
      onAccept()
      return
    }
    if (acceptBusy || rejectBusy || isLinking) return
    setAcceptBusy(true)
    try {
      const res = await linkToJob(emailId, suggested.job_id)
      if (res) {
        toast(`Linked to ${suggested.job_label}`, 'success')
        onConfirmed?.()
      } else {
        toast('Could not link email - try the Link to Job modal', 'error')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to link email'
      toast(msg, 'error')
    } finally {
      setAcceptBusy(false)
    }
  }

  const handleReject = async (event: React.MouseEvent) => {
    event.stopPropagation()
    if (onReject) {
      onReject()
      return
    }
    if (acceptBusy || rejectBusy || isLinking) return
    setRejectBusy(true)
    try {
      const res = await rejectSuggestion(emailId, suggested.job_id)
      if (res) {
        toast(`Dismissed suggestion (${suggested.job_label})`, 'success')
        onDismissed?.()
      } else {
        toast('Could not dismiss suggestion - try again', 'error')
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to dismiss suggestion'
      toast(msg, 'error')
    } finally {
      setRejectBusy(false)
    }
  }

  const tooltip = `Suggested: ${suggested.job_label} (${KIND_LABEL[suggested.kind]})`
  const anyBusy = acceptBusy || rejectBusy || isLinking

  // v003.1 (2026-06-22, operator "the tab view is completely fucked"): the chip
  // is ONE fused unit at the uniform CHIP_BASE size — a [sparkle · label]
  // segment and a divider-separated [✓ ✗] segment — instead of two loose
  // round glyph buttons floating off the end of an 8px pill (which read as
  // free-floating controls in the strip seam). The reject is calmed to muted
  // amber→red on hover (the inviting ✓ stays emerald), matching the framed
  // ChipReview so the operator learns ONE accept/reject affordance.
  return (
    <div
      role="group"
      aria-label={`Suggested job ${suggested.job_label}`}
      title={tooltip}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'inline-flex items-stretch overflow-hidden rounded-full border border-dashed transition-colors max-w-[240px]',
        'border-amber-400/70 bg-amber-50 text-amber-800',
        'dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-300',
      )}
    >
      <span className="inline-flex min-w-0 items-center gap-1 py-0.5 pl-2 pr-1.5 text-[10px] font-medium">
        <Sparkles className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">{suggested.job_label}</span>
      </span>
      <button
        type="button"
        onClick={handleAccept}
        disabled={anyBusy}
        title="Accept this suggestion (link email to job)"
        aria-label={`Accept suggested job ${suggested.job_label}`}
        className={cn(
          'inline-flex items-center justify-center px-2 touch-manipulation transition-colors',
          'border-l border-amber-400/50 dark:border-amber-500/40',
          'text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-300',
          'disabled:opacity-40 disabled:cursor-wait',
        )}
      >
        {acceptBusy ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Check className="h-3.5 w-3.5" />
        )}
      </button>
      <button
        type="button"
        onClick={handleReject}
        disabled={anyBusy}
        title="Dismiss this suggestion (won't be suggested again for this sender)"
        aria-label={`Dismiss suggested job ${suggested.job_label}`}
        className={cn(
          'inline-flex items-center justify-center px-2 touch-manipulation transition-colors',
          'border-l border-amber-400/50 dark:border-amber-500/40',
          'text-amber-700/70 hover:bg-red-500/15 hover:text-red-600',
          'dark:text-amber-300/70 dark:hover:text-red-300',
          'disabled:opacity-40 disabled:cursor-wait',
        )}
      >
        {rejectBusy ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <X className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  )
}
